import {
  JobOfferEmbedDetailsShowingV2ResponseDto,
  JobOfferEmbedListResponseDto,
  JobOfferEmbedListShowingResponseDto,
} from "@/lib/interfaces/embed";
import { Box, Flex, Spinner } from "@chakra-ui/react";
import { ExternalLink } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useUser } from "../../hooks/use-user";
import useApiRequest from "../../lib/hooks/useRequest";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from "../../ui/components/ui/select";
import { IframeSettingsDesignDraft, useIframeSettingsPreview } from "./IframeSettingsPreviewContext";
import { getMockJobPostV2 } from "./job-posts-v2-mocks";
import { EMBED_JOB_PREVIEW_SCOPE_CLASS } from "./job-posts-v2/embed-visual-settings";
import { JobPostsV2 } from "./JobPostsV2";

/** Select value for the Karriereportal job-list iframe preview. */
const KARRIEREPORTAL_PREVIEW_ID = "__iframe_settings_karriereportal__";

/** Select value for the always-available fully populated demo preview (not a real job id). */
const MOCK_PREVIEW_JOB_ID = "__iframe_settings_job_demo__";

function buildKarriereportalPreviewUrl(organizationId: string, design: IframeSettingsDesignDraft): string {
  const baseUrl = `${window.location.protocol}//${window.location.host}/job-angebote/iframe/${organizationId}`;
  const params = new URLSearchParams();

  if (design.color && design.color !== "#000000") {
    params.append("color", design.color);
  }
  if (design.showImage !== undefined) {
    params.append("showImage", design.showImage.toString());
  }
  if (design.customCss?.trim()) {
    params.append("customCss", design.customCss);
  }

  return params.toString() ? `${baseUrl}?${params.toString()}` : baseUrl;
}

function buildKarriereportalPublicPath(organizationId: string, design: IframeSettingsDesignDraft): string {
  const basePath = `/job-angebote/iframe/${organizationId}`;
  const params = new URLSearchParams();

  if (design.color && design.color !== "#000000") {
    params.append("color", design.color);
  }
  if (design.showImage !== undefined) {
    params.append("showImage", design.showImage.toString());
  }
  if (design.customCss?.trim()) {
    params.append("customCss", design.customCss);
  }

  return params.toString() ? `${basePath}?${params.toString()}` : basePath;
}

export function IFrameSettingsJobPreview() {
  const { apiRequest } = useApiRequest();
  const apiRequestRef = useRef(apiRequest);
  apiRequestRef.current = apiRequest;

  const { user } = useUser();
  const { designDraft, textDraft } = useIframeSettingsPreview();
  const organizationId = user?.organizationId || "";

  const [listLoading, setListLoading] = useState(true);
  const [jobOffers, setJobOffers] = useState<JobOfferEmbedListResponseDto[]>([]);
  const [previewSelection, setPreviewSelection] = useState(KARRIEREPORTAL_PREVIEW_ID);
  const [detailLoading, setDetailLoading] = useState(false);
  const [baseDetail, setBaseDetail] = useState<JobOfferEmbedDetailsShowingV2ResponseDto | null>(null);
  /** `baseDetail` belongs to this job id; ignore merge until it matches `previewSelection`. */
  const [loadedJobId, setLoadedJobId] = useState<string | null>(null);
  const [usingMock, setUsingMock] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const detailRequestSeq = useRef(0);
  const previewIframeRef = useRef<HTMLIFrameElement>(null);

  const isKarriereportalMode = previewSelection === KARRIEREPORTAL_PREVIEW_ID;

  const karriereportalIframeSrc = useMemo(
    () => (organizationId ? buildKarriereportalPreviewUrl(organizationId, designDraft) : ""),
    [designDraft, organizationId],
  );

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!isNaN(event.data) && previewIframeRef.current) {
        previewIframeRef.current.style.height = event.data + "px";
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  useEffect(() => {
    if (!organizationId) {
      setListLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      setListLoading(true);
      const res = await apiRequestRef.current<JobOfferEmbedListShowingResponseDto>(
        `embed/${organizationId}/job-offers`,
        "GET",
        {
          withoutAuth: true,
        },
      );
      if (cancelled) return;

      const offers = res.data?.jobOffers ?? [];
      setJobOffers(offers);
      setPreviewSelection((prev) => {
        if (prev === KARRIEREPORTAL_PREVIEW_ID || prev === MOCK_PREVIEW_JOB_ID) return prev;
        if (prev && offers.some((o) => o._id === prev)) return prev;
        return KARRIEREPORTAL_PREVIEW_ID;
      });
      setDetailError(false);
      setListLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    if (listLoading) return;
    if (isKarriereportalMode) return;
    if (!previewSelection) return;

    if (previewSelection === MOCK_PREVIEW_JOB_ID) {
      detailRequestSeq.current += 1;
      setDetailLoading(false);
      setBaseDetail(getMockJobPostV2());
      setLoadedJobId(MOCK_PREVIEW_JOB_ID);
      setUsingMock(true);
      setDetailError(false);
      return;
    }

    if (jobOffers.length === 0) return;

    if (!organizationId) return;

    const jobId = previewSelection;
    const seq = ++detailRequestSeq.current;
    setDetailLoading(true);
    setDetailError(false);

    (async () => {
      try {
        const res = await apiRequestRef.current<JobOfferEmbedDetailsShowingV2ResponseDto>(
          `embed/${organizationId}/job-offers/${jobId}`,
          "GET",
          { withoutAuth: true },
          "v2.0",
        );
        if (seq !== detailRequestSeq.current) return;
        if (res.data) {
          setBaseDetail(res.data);
          setLoadedJobId(jobId);
          setUsingMock(false);
        } else {
          setBaseDetail(getMockJobPostV2());
          setLoadedJobId(jobId);
          setUsingMock(true);
          setDetailError(true);
        }
      } finally {
        if (seq === detailRequestSeq.current) {
          setDetailLoading(false);
        }
      }
    })();
  }, [isKarriereportalMode, jobOffers.length, listLoading, organizationId, previewSelection]);

  const previewReady = Boolean(
    !isKarriereportalMode &&
    baseDetail &&
    (usingMock || loadedJobId === previewSelection) &&
    (previewSelection === MOCK_PREVIEW_JOB_ID || jobOffers.length > 0),
  );

  if (!organizationId) {
    return (
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-500">Organisation nicht geladen.</p>
      </div>
    );
  }

  if (listLoading) {
    return (
      <div className="flex-1 min-w-0 flex items-center justify-center min-h-[480px] border border-gray-200 rounded-lg bg-gray-50">
        <Spinner size="xl" color="primary.500" />
      </div>
    );
  }

  const previewJobId = usingMock
    ? (baseDetail?.jobOfferPreview._id ?? "preview-mock")
    : previewSelection || baseDetail?.jobOfferPreview._id || "";

  const isDemoPreview = previewSelection === MOCK_PREVIEW_JOB_ID;
  const isRealJobPreview = !isKarriereportalMode && !isDemoPreview && Boolean(previewSelection);

  const canOpenPublicPage = isKarriereportalMode || isRealJobPreview;

  const publicPageHref = isKarriereportalMode
    ? buildKarriereportalPublicPath(organizationId, designDraft)
    : isRealJobPreview
      ? `/job-angebote/iframe/${organizationId}/${previewSelection}`
      : undefined;

  return (
    <div className="flex-1 min-w-0 flex flex-col min-h-0">
      <h3 className="text-lg font-semibold mb-4">Live Vorschau</h3>
      <p className="text-xs text-gray-600 mb-2">
        Wählen Sie zwischen der Karriereportal-Übersicht (alle Stellen) und der Einzelansicht einer Stelle. Design- und
        CSS-Anpassungen werden in der Vorschau übernommen. Zum Einbinden auf Ihrer Website verwenden Sie den
        bereitgestellten Embed-Code.
      </p>

      <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-end sm:gap-3">
        <div className="block w-full min-w-0 max-w-md flex-1">
          <span className="text-sm font-medium text-gray-700">Vorschau</span>
          <Select value={previewSelection} onValueChange={setPreviewSelection}>
            <SelectTrigger className="mt-1 w-full">
              <SelectValue placeholder="Vorschau wählen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={KARRIEREPORTAL_PREVIEW_ID} className="cursor-pointer">
                Karriereportal
              </SelectItem>
              <SelectSeparator />
              <SelectGroup>
                <SelectLabel>Stellenvorschau</SelectLabel>
                <SelectItem value={MOCK_PREVIEW_JOB_ID} className="cursor-pointer">
                  Demo Vorschau
                </SelectItem>
                {jobOffers.map((j) => (
                  <SelectItem key={j._id} value={j._id} className="cursor-pointer">
                    {j.title}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
        </div>
        <a
          href={publicPageHref}
          target="_blank"
          rel="noopener noreferrer"
          title={
            canOpenPublicPage
              ? isKarriereportalMode
                ? "Karriereportal in neuem Tab öffnen"
                : "Stellenanzeige in neuem Tab öffnen"
              : "Nur für Karriereportal oder eine echte Stelle verfügbar (nicht für die Demo-Vorschau)."
          }
          aria-disabled={!canOpenPublicPage}
          className={`inline-flex h-9 shrink-0 items-center gap-2 rounded-md border px-3 text-sm font-medium shadow-sm transition-colors ${
            canOpenPublicPage
              ? "border-primary-600 bg-primary-50 text-primary-700 hover:bg-primary-100 focus-visible:outline focus-visible:ring-2 focus-visible:ring-primary-500"
              : "pointer-events-none cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400"
          }`}
          onClick={(e) => {
            if (!canOpenPublicPage) e.preventDefault();
          }}
        >
          <ExternalLink className="h-4 w-4 shrink-0" aria-hidden />
          Seite öffnen
        </a>
      </div>

      {!isKarriereportalMode && detailError && jobOffers.length > 0 ? (
        <p className="text-xs text-amber-700 mb-2">
          Stelle konnte nicht geladen werden – es werden Beispieldaten angezeigt.
        </p>
      ) : null}

      <div className="border border-gray-300 rounded-lg overflow-hidden bg-white shadow-sm flex-1 min-h-0 flex flex-col">
        {isKarriereportalMode ? (
          <iframe
            id="live-preview-iframe"
            ref={previewIframeRef}
            src={karriereportalIframeSrc}
            title="Karriereportal Vorschau"
            width="100%"
            style={{ border: "none", overflow: "hidden", minHeight: "320px" }}
            className="w-full"
          />
        ) : (
          <div
            className={`${EMBED_JOB_PREVIEW_SCOPE_CLASS} flex-1 overflow-y-auto max-h-[calc(100vh-220px)] relative min-h-[320px]`}
          >
            {detailLoading && jobOffers.length > 0 && !previewReady ? (
              <Flex className="absolute inset-0 items-center justify-center bg-gray-50 z-10">
                <Spinner size="lg" color="primary.500" />
              </Flex>
            ) : null}
            {detailLoading && jobOffers.length > 0 && previewReady ? (
              <Flex className="absolute inset-0 items-center justify-center bg-white/70 z-10">
                <Spinner size="lg" color="primary.500" />
              </Flex>
            ) : null}
            {previewReady && baseDetail ? (
              <Box className="iframe-settings-job-preview">
                <JobPostsV2
                  organizationId={organizationId}
                  id={previewJobId}
                  previewRawDetail={baseDetail}
                  previewDesign={designDraft}
                  previewTextDraft={textDraft}
                  readOnlyApplication
                  settingsPreview={{
                    showMockBanner: usingMock,
                    disableSimilarJobNavigation: true,
                    contactSection: textDraft.contact,
                  }}
                />
              </Box>
            ) : jobOffers.length > 0 ? (
              <Flex className="min-h-[320px] items-center justify-center text-sm text-gray-500">
                Stelle wird geladen…
              </Flex>
            ) : null}
          </div>
        )}
      </div>

      <p className="text-xs text-gray-500 mt-2">
        Die Vorschau aktualisiert sich automatisch, nachdem Sie 0,5 Sekunden lang keine Änderungen vorgenommen haben.
      </p>
    </div>
  );
}
